import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { FlexyForm, FlexySelectFormStructure, FormStructure } from '@europrocurement/flexy-form';
import { FieldValues, SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { OptionType } from '@europrocurement/flexy-components';
import { useCurrentDossierCtx } from '@europrocurement/l2d-modules';
import { FormInputInformations } from '@europrocurement/flexy-form/components/ControlledSelect/ControlledSelect';
import { LegalAnnoucementType, useDossierLegalAnnoucements } from '../../hooks';

export type AddLegalAnnoucementFormProps = {
    processing: boolean;
    onSubmit: (data: AddLegalAnnoucementFormData) => void;
    onCancel: () => void;
};

export type AddLegalAnnoucementFormData = {
    title: string;
    prestationId?: number;
    typeAnnonce?: number;
};

export type SubmittedLegalAnnoucementFormData = {
    title: string;
    prestationId: Array<number>;
    typeAnnonce: OptionType;
};

const AddLegalAnnoucementForm: React.FunctionComponent<AddLegalAnnoucementFormProps> = function (
    props,
) {
    const { processing, onSubmit, onCancel } = props;
    const { dossier } = useCurrentDossierCtx();
    const { getLegalAnnoucementTypes } = useDossierLegalAnnoucements();
    const [types, setTypes] = useState<Array<LegalAnnoucementType>>([]);

    useEffect(() => {
        getLegalAnnoucementTypes()
            .then((result) => {
                setTypes(result);
            })
            .catch((err) => {
                console.error(err);
                setTypes([]);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const prestationChoices = dossier
        ? dossier.formalites
              .filter((formalite) => formalite.prestationId.length > 0)
              .map((formalite) => ({
                  value: formalite.prestationId[0],
                  label: formalite.libelle,
              }))
        : [];

    // Update Name of title input on typeAnnonce selection
    const handleSelectType = (
        e: React.SyntheticEvent,
        fieldContext: FormInputInformations,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formContext: UseFormReturn<FieldValues, any, FieldValues>,
    ) => {
        const typeAnnonceInputValue = formContext.getValues('typeAnnonce');
        if (typeAnnonceInputValue.label) {
            formContext.setValue('title', typeAnnonceInputValue.label);
        }
    };

    const handleSubmit: SubmitHandler<SubmittedLegalAnnoucementFormData> = (
        formData: SubmittedLegalAnnoucementFormData,
    ) => {
        let prestationId;

        if (prestationChoices.length === 1) {
            prestationId = prestationChoices[0].value;
        } else if (formData?.prestationId[0]) {
            prestationId = formData?.prestationId[0];
        }

        onSubmit({
            title: formData.title,
            prestationId,
            typeAnnonce: formData?.typeAnnonce?.value as number,
        });
    };

    const formStructure: FormStructure[] = [
        {
            type: 'select',
            name: 'typeAnnonce',
            inputlabel: "Type d'annonce",
            options: types.map((type) => ({
                value: type.id,
                label: type.label,
            })),
            rules: {
                required: 'Ce champ est requis',
            },
            onChangeInput: handleSelectType,
        } as FlexySelectFormStructure,
        {
            type: 'text',
            name: 'title',
            inputlabel: 'Nom de la démarche',
            rules: {
                required: 'Ce champ est requis',
            },
        },
        ...(prestationChoices.length > 1
            ? [
                  {
                      type: 'selectItems',
                      name: 'prestationId',
                      mode: 'inline',
                      inputlabel: 'Formalité associée',
                      colInline: 6,
                      items: prestationChoices,
                      multiple: false,
                      rules: {
                          required: 'Ce champ est requis',
                      },
                  } as FormStructure,
              ]
            : []),
    ];

    const formContext = useForm<FieldValues>({
        mode: 'onChange',
    });

    const formProps = {
        formContext,
        onSubmit: handleSubmit,
        submitButton: {
            displayed: false,
        },
    };

    const submitBtnSx = {
        backgroundColor: 'primary.dark',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '0 10px',
    };

    return (
        <Box>
            <Typography variant="l2dh6">Ajouter une annonce légale</Typography>
            <Box
                sx={{
                    padding: '26px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '10px',
                }}
            >
                <FlexyForm
                    formStructure={formStructure}
                    {...formProps}
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    columnGap: '16px',
                }}
            >
                <Button
                    variant="text"
                    sx={{
                        padding: '16px 24px',
                        alignItems: 'center',
                        color: 'primary.dark',
                    }}
                    onClick={onCancel}
                    disabled={processing}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    sx={submitBtnSx}
                    onClick={formContext.handleSubmit(handleSubmit)}
                    disabled={processing}
                >
                    {processing && (
                        <CircularProgress
                            color="inherit"
                            size="16px"
                        />
                    )}
                    {!processing && (
                        <FaOptionIcon
                            icon={['fas', 'plus']}
                            sx={{ lineHeight: 0 }}
                        />
                    )}
                    Ajouter l&lsquo;annonce légale
                </Button>
            </Box>
        </Box>
    );
};

export default AddLegalAnnoucementForm;
